import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tooltip, CircularProgress,
  Stack, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  useTheme, Autocomplete,
} from '@mui/material';
import { Plus, Pencil, Trash2, Search, X, Scale, Package, Tag } from 'lucide-react';import { subscribeToProducts, addProduct, updateProduct, deleteProduct } from '../firebase/productService';

const EMPTY_FORM = {
  name: '', nameTa: '', category: '', sellType: 'weight', imageUrl: '', costPrice: '', sellingPrice: '',
};

const Products = ({ currentUser, permissions }) => {
  const theme = useTheme();
  const isAdmin = permissions?.userType === 'admin';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const unsub = subscribeToProducts((data) => {
      setProducts(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const categories = ['All', ...new Set(products.map((p) => p.category).filter(Boolean)).values()].sort((a, b) => a === 'All' ? -1 : a.localeCompare(b));

  const existingCategories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const filtered = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'All' || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setImagePreview(null);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      nameTa: p.nameTa || '',
      category: p.category || '',
      sellType: p.sellType || 'weight',
      imageUrl: p.imageUrl || '',
      costPrice: (p.costPrice ?? '').toString(),
      sellingPrice: (p.sellingPrice ?? '').toString(),
    });
    setImagePreview(p.imageUrl || null);
    setError('');
    setDialogOpen(true);
  };

  const validate = () => {
    if (!form.name.trim()) return 'Product name is required';
    if (!form.nameTa.trim()) return 'Tamil product name is required';
    if (!form.category.trim()) return 'Category is required';
    if (form.costPrice === '' || isNaN(parseFloat(form.costPrice)) || parseFloat(form.costPrice) < 0) return 'Cost price is required';
    if (form.sellingPrice === '' || isNaN(parseFloat(form.sellingPrice)) || parseFloat(form.sellingPrice) < 0) return 'Selling price is required';
    return '';
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    const data = {
      name: form.name.trim(),
      nameTa: form.nameTa.trim(),
      category: form.category.trim(),
      sellType: form.sellType,
      imageUrl: form.imageUrl || '',
      costPrice: parseFloat(form.costPrice) || 0,
      sellingPrice: parseFloat(form.sellingPrice) || 0,
    };
    const result = editing
      ? await updateProduct(editing.id, data)
      : await addProduct(data);
    setSaving(false);
    if (result.success) {
      setSuccess(editing ? 'Product updated' : 'Product added');
      setDialogOpen(false);
      setImagePreview(null);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setSaving(true);
    await deleteProduct(deleteDialog.id);
    setSaving(false);
    setDeleteDialog(null);
    setSuccess('Product deleted');
    setTimeout(() => setSuccess(''), 3000);
  };

  const f = (v) => setForm((prev) => ({ ...prev, ...v }));

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result;
      f({ imageUrl: base64String });
      setImagePreview(base64String);
      setUploadingImage(false);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    f({ imageUrl: '' });
    setImagePreview(null);
  };
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Products</Typography>
          <Typography variant="body2" color="text.secondary">
            Define your product catalog — names, categories, sell type and prices
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={openAdd}>
            Add Product
          </Button>
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Search + category filter */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small" placeholder="Search products..." value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ maxWidth: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}><X size={14} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <Chip key={cat} label={cat} size="small"
              color={categoryFilter === cat ? 'primary' : 'default'}
              variant={categoryFilter === cat ? 'filled' : 'outlined'}
              onClick={() => setCategoryFilter(cat)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Products', value: products.length },
          { label: 'Weight-based', value: products.filter((p) => p.sellType === 'weight').length },
          { label: 'Piece-based', value: products.filter((p) => p.sellType === 'piece').length },
          { label: 'Categories', value: existingCategories.length },
        ].map((s) => (
          <Paper key={s.label} sx={{ px: 2.5, py: 1.5, textAlign: 'center', minWidth: 110 }}>
            <Typography variant="h5" fontWeight={700} color="primary.main">{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Package size={48} color={theme.palette.text.disabled} style={{ marginBottom: 12 }} />
          <Typography variant="h6" color="text.secondary">
            {search ? 'No products match your search' : 'No products yet'}
          </Typography>
          {!search && isAdmin && (
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={openAdd} sx={{ mt: 2 }}>
              Add First Product
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tamil Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sell Type</TableCell>
                {isAdmin && <TableCell align="right" sx={{ fontWeight: 700 }}>Cost / Selling (LKR)</TableCell>}
                {isAdmin && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{p.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{p.nameTa}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip icon={<Tag size={12} />} label={p.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={p.sellType === 'weight' ? <Scale size={12} /> : <Package size={12} />}
                      label={p.sellType === 'weight' ? 'By Weight' : 'By Piece'}
                      size="small"
                      color={p.sellType === 'weight' ? 'warning' : 'info'}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Typography variant="body2">{(p.costPrice ?? 0).toLocaleString()} / {(p.sellingPrice ?? 0).toLocaleString()}</Typography>
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(p)}><Pencil size={16} /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteDialog(p)}><Trash2 size={16} /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Product Name (English) *" value={form.name}
              onChange={(e) => f({ name: e.target.value })}
              fullWidth autoFocus
            />

            <TextField
              label="Product Name (Tamil) - பொருளின் பெயர் *" value={form.nameTa}
              onChange={(e) => f({ nameTa: e.target.value })}
              fullWidth
              placeholder="e.g. முறுக்கு, மிட்டாய், வேர்க்கடலை"
            />

            <Autocomplete
              freeSolo
              options={existingCategories}
              value={form.category}
              onInputChange={(_, v) => f({ category: v })}
              renderInput={(params) => (
                <TextField {...params} label="Category *" placeholder="e.g. Murukku, Sweets, Peanut" />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Cost Price (LKR) *" type="number" value={form.costPrice}
                onChange={(e) => f({ costPrice: e.target.value })}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">LKR</InputAdornment> }}
                helperText="Used to calculate profit in Monthly P&L"
              />
              <TextField
                label="Selling Price (LKR) *" type="number" value={form.sellingPrice}
                onChange={(e) => f({ sellingPrice: e.target.value })}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">LKR</InputAdornment> }}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Sell Type *</InputLabel>
              <Select value={form.sellType} label="Sell Type *" onChange={(e) => f({ sellType: e.target.value })}>
                <MenuItem value="weight">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Scale size={16} /> By Weight (kg)
                  </Box>
                </MenuItem>
                <MenuItem value="piece">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Package size={16} /> By Piece — fixed price per item
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Product Image (Optional)
              </Typography>
              {imagePreview ? (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Product preview"
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={removeImage}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                  />
                  {uploadingImage ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Click to upload or drag and drop
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PNG, JPG, GIF up to 2MB
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Shop retail/wholesale pricing is still set in <strong>Stock Transfer</strong>. The cost/selling price above is only used for the Monthly P&L report.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Product?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteDialog?.name}</strong>? This won't affect existing inventory or sales records.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialog(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
